// The edits the Gantt's right-click menu makes on the DRAFT rows (owner,
// 2026-10-10). Pure, so each one is tested without a chart, and each one is
// the same change the plan table would make - the Gantt is a second way to
// edit the same rows, never a second model of them. Save schedule persists
// both the same way.
import { linkOf } from "helpers/schedule/predecessors";
import { subtreeEnd, hasChildrenAt } from "./scheduleEditOps";

const linksOf = (preds) => (preds || []).map(linkOf).filter(Boolean);

// Does `key` (transitively) follow `target`? Walks predecessors only.
function follows(rows, key, target) {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const seen = new Set();
  const stack = [key];
  while (stack.length) {
    const k = stack.pop();
    if (k === target) return true;
    if (seen.has(k)) continue;
    seen.add(k);
    const r = byKey.get(k);
    if (r) linksOf(r.predecessors).forEach((l) => stack.push(l.key));
  }
  return false;
}

// The keys of a row's own group above it and of everything under it.
function familyOf(rows, key) {
  const i = rows.findIndex((r) => r.key === key);
  const out = new Set();
  if (i < 0) return out;
  rows.slice(i + 1, subtreeEnd(rows, i)).forEach((r) => out.add(r.key));
  let level = rows[i].indent || 0;
  for (let j = i - 1; j >= 0 && level > 0; j--) {
    const ind = rows[j].indent || 0;
    if (ind < level) { out.add(rows[j].key); level = ind; }
  }
  return out;
}

// "Place after X": this activity starts when X finishes (a finish-to-start
// link, the plan table's default), and its pinned start is released so it
// actually moves there - a pin would hold it where it was, because the
// engine lets a later manual start win. A summary rolls up from its rows
// and takes no links, so it is refused rather than linked to no effect.
export function placeAfter(rows, key, targetKey) {
  if (!key || !targetKey) return { error: "Pick an activity" };
  if (key === targetKey) return { error: "An activity cannot follow itself" };
  const i = rows.findIndex((r) => r.key === key);
  const t = rows.findIndex((r) => r.key === targetKey);
  if (i < 0 || t < 0) return { error: "That activity is not in the schedule" };
  if (hasChildrenAt(rows, i)) return { error: `"${rows[i].name}" is a group - its dates come from the activities under it. Place one of those instead.` };
  if (familyOf(rows, key).has(targetKey)) return { error: "An activity cannot follow its own group or anything inside it" };
  if (follows(rows, targetKey, key)) return { error: `"${rows[t].name}" already comes after "${rows[i].name}" - this would make a loop` };
  const links = linksOf(rows[i].predecessors);
  const had = links.find((l) => l.key === targetKey);
  if (had && had.type === "FS" && !had.lagDays && !rows[i].manualStart) return { error: `"${rows[i].name}" already follows "${rows[t].name}"` };
  const next = [...links.filter((l) => l.key !== targetKey), { key: targetKey, type: "FS", lagDays: 0 }];
  return {
    rows: rows.map((r, j) => (j === i ? { ...r, predecessors: next, manualStart: "", _loadedEnd: undefined } : r)),
  };
}

// "Place before X" is X placed after this one.
export function placeBefore(rows, key, targetKey) {
  return placeAfter(rows, targetKey, key);
}

// Start or finish from a picked day. The OTHER end stays where it is, so the
// bar expands or retracts - the same as dragging that end. Milestones have
// one date: either action moves them. `wdb` = SchedulerPage's
// workingDaysBetween(a, b, calendarId).
export function datePatch(row, computedRow, which, date, wdb) {
  if (!date) return { error: "No day picked" };
  const zero = computedRow && computedRow.milestone;
  if (zero) return { patch: { manualStart: date } };
  const start = computedRow && computedRow.startDate;
  const end = computedRow && computedRow.endDate;
  if (which === "start") {
    if (end && date > end) return { error: `That is after its finish (${end}). Set the finish first, or drag the bar.` };
    return { patch: { manualStart: date, ...(end ? { durationDays: Math.max(1, wdb(date, end, row.calendarId || null)) } : {}) } };
  }
  if (start && date < start) return { error: `That is before its start (${start}). Set the start first, or drag the bar.` };
  return { patch: { durationDays: Math.max(1, wdb(start || date, date, row.calendarId || null)) } };
}

// Delete one activity - and, when asked, everything under it - and every
// link that pointed at what went (the plan table's bulk delete, for one row).
export function deleteActivity(rows, key, withChildren) {
  const i = rows.findIndex((r) => r.key === key);
  if (i < 0) return { rows, removed: 0 };
  const kill = new Set(withChildren ? rows.slice(i, subtreeEnd(rows, i)).map((r) => r.key) : [key]);
  const out = rows.filter((r) => !kill.has(r.key)).map((r) => {
    const preds = r.predecessors || [];
    const kept = preds.filter((p) => !kill.has(typeof p === "string" ? p : p && p.key));
    return kept.length === preds.length ? r : { ...r, predecessors: kept };
  });
  return { rows: out, removed: kill.size };
}
